// src/pages/UserProfilePage.jsx
import React, { useState, useEffect } from 'react';
import GlassCard from '../../components/GlassCard';
import '../styles/UserProfilePage.css';
import '../styles/LeftSidebar.css';
import HeaderPlatform from '../components/HeaderPlatform';
import LeftSidebar from '../components/LeftSidebar';
import FooterPlatform from '../components/FooterPlatform';
import { userProfilePageContent } from '../data/userProfilePageContent';
import { useCurrentLang } from '../../hooks/useCurrentLang';
import BasicInfoBox from '../components/BasicInfoBox';
import DisplayRow from '../components/DisplayRow';
import { useAuth, getUserProfilePageData, saveUserProfilePageData } from '../../auth';
import { profileOptions } from '../data/profileOptions';
import Warning from '../components/Warnings';

const staticProfileStats = {
  currentExercisePlan: 'Leg Exercises',
  startingDate: '01.01.2026',
  discoveredExercises: 37,
  completedSessions: 34,
  totalExercisesDone: 105,
  dayStreak: 21,
  subscriptionPlan: 'Standard',
  nextPaymentDate: '21.02.2026',
  validUntil: '22.02.2026',
  cardCode: '**** **** 1234',
  cardOwner: 'Gašper Bračko',
};

export default function UserProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const currentLang = useCurrentLang();
  const t = userProfilePageContent[currentLang];
  const v = profileOptions[currentLang];

  const { user, userId } = useAuth();
  const [profile, setProfile] = useState(null);
  const [originalProfile, setOriginalProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const ageGroupOptions = [
    { value: '1-17', label: '1-17' },
    { value: '18-34', label: '18-34' },
    { value: '35-49', label: '35-49' },
    { value: '50-64', label: '50-64' },
    { value: '65-75', label: '65-75' },
    { value: '75+', label: '75+' },
  ];

  const countryOptions = [
    { value: 'Slovenia', label: v.slovenia },
    { value: 'Croatia', label: v.croatia },
    { value: 'Austria', label: v.austria },
    { value: 'Germany', label: v.germany },
    { value: 'Italy', label: v.italy },
    { value: 'England', label: v.england },
  ];

  const languageOptions = [
    { value: 'Slovene', label: v.slovenscina },
    { value: 'English', label: v.english },
  ];

  const timeZoneOptions = [
    { value: 'Europe/Ljubljana', label: v.ljubljanaTimeZone },
    { value: 'Europe/London', label: v.londonTimeZone },
    { value: 'Europe/Berlin', label: v.berlinTimeZone },
    { value: 'America/New_York', label: v.newYorkTimeZone },
    { value: 'Asia/Tokyo', label: v.tokyoTimeZone },
  ];

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    getUserProfilePageData()
      .then((data) => {
        if (!isMounted) return;
        setProfile(data);
        setOriginalProfile(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error(err);
        setSaveMessage('Failed to load profile.');
      });

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNotificationChange = (field) => {
    if (!isEditing) return;

    setProfile((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    if (!profile) return;

    try {
      setSaving(true);
      setSaveMessage('');
      await saveUserProfilePageData(profile);
      setOriginalProfile(profile);
      setSaveMessage('Saved successfully.');
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setSaveMessage('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEditing = () => {
    if (isEditing) {
      setProfile(originalProfile);
      setSaveMessage('');
      setIsEditing(false);
      return;
    }

    setSaveMessage('');
    setIsEditing(true);
  };

  if (!profile) return <div>Loading...</div>;

  const missingPersonalInfo =
    profile.age_group === '' ||
    profile.country === '' ||
    profile.time_zone === '';

  return (
    <div className="revimo-layout">
      <HeaderPlatform />
      <main className="revimo-main">
        <LeftSidebar />
        <div className="revimo-page-content">
          <form
            className="user-profile-page"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <BasicInfoBox />

            {missingPersonalInfo && <Warning warningType="MissingPersonalInfo" />}

            {saveMessage && <div className="profile-save-message">{saveMessage}</div>}

            <GlassCard style={{ padding: '10px', maxWidth: '775px', marginLeft: '50px' }}>
              <div className="profile-section">
                <div className="profile-col profile-col-left">
                  <DisplayRow label={t.name} value={user?.name || ''} />

                  <EditableRow
                    label={t.ageG}
                    editable
                    value={profile?.age_group || ''}
                    isEditing={isEditing}
                    onChange={(v) => handleProfileChange('age_group', v)}
                    options={ageGroupOptions}
                  />

                  <EditableRow
                    label={t.country}
                    editable
                    value={profile?.country || ''}
                    isEditing={isEditing}
                    onChange={(v) => handleProfileChange('country', v)}
                    options={countryOptions}
                  />

                  <EditableRow
                    label={t.telNum}
                    editable
                    value={profile?.phone_number || ''}
                    isEditing={isEditing}
                    onChange={(v) => handleProfileChange('phone_number', v)}
                  />

                  <EditableRow
                    label={t.email}
                    value={user?.email || ''}
                    isEditing={false}
                  />
                </div>

                <div className="profile-col profile-col-right">
                  <EditableRow
                    label={t.timeZ}
                    editable
                    value={profile?.time_zone || ''}
                    isEditing={isEditing}
                    onChange={(v) => handleProfileChange('time_zone', v)}
                    options={timeZoneOptions}
                  />

                  <EditableRow
                    label={t.prefLang}
                    editable
                    value={profile?.preferred_language || ''}
                    isEditing={isEditing}
                    onChange={(v) => handleProfileChange('preferred_language', v)}
                    options={languageOptions}
                  />

                  <div className="profile-edit-info">
                    <span className="required-text">* fields are required</span>
                    <button
                      type="button"
                      className="profile-btn"
                      onClick={toggleEditing}
                      disabled={saving}
                    >
                      {isEditing ? t.cancel : t.editData}
                    </button>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard style={{ padding: '10px', maxWidth: '775px', marginLeft: '50px' }}>
              <div className="profile-section">
                <div className="profile-col profile-col-left">
                  <DisplayRow label={t.currExePlan} value={staticProfileStats.currentExercisePlan} />
                  <DisplayRow label={t.startDate} value={staticProfileStats.startingDate} />
                  <DisplayRow label={t.discExe} value={staticProfileStats.discoveredExercises} />
                </div>

                <div className="profile-col profile-col-right">
                  <DisplayRow label={t.complSess} value={staticProfileStats.completedSessions} />
                  <DisplayRow label={t.totalExeDone} value={staticProfileStats.totalExercisesDone} />
                  <DisplayRow label={t.streak} value={staticProfileStats.dayStreak} />
                </div>
              </div>
            </GlassCard>

            <GlassCard style={{ padding: '10px', maxWidth: '775px', marginLeft: '50px' }}>
              <div className="profile-section">
                <div className="profile-col profile-col-left">
                  <DisplayRow label={t.subPlan} value={staticProfileStats.subscriptionPlan} />
                  <DisplayRow label={t.nextPayDay} value={staticProfileStats.nextPaymentDate} />
                  <DisplayRow label={t.validTill} value={staticProfileStats.validUntil} />
                </div>

                <div className="profile-col profile-col-right">
                  <DisplayRow label={t.payCardCode} value={staticProfileStats.cardCode} />
                  <DisplayRow label={t.cardOwner} value={staticProfileStats.cardOwner} />

                  <div className="profile-payment-actions">
                    <button type="button" className="profile-btn">
                      {t.manageSub}
                    </button>
                    <button type="button" className="profile-btn">
                      {t.updatePayment}
                    </button>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard style={{ padding: '10px', maxWidth: '775px', marginLeft: '50px' }}>
              <div className="profile-section notifications-section">
                <div className="profile-col profile-col-left">
                  <div className="notifications-title">{t.emailNoti}</div>

                  <NotificationRow
                    label={t.exeReminders}
                    checked={!!profile.email_exercise_reminders}
                    onChange={() => handleNotificationChange('email_exercise_reminders')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.sessionReminders}
                    checked={!!profile.email_session_reminders}
                    onChange={() => handleNotificationChange('email_session_reminders')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.productUpdates}
                    checked={!!profile.email_product_updates}
                    onChange={() => handleNotificationChange('email_product_updates')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.subReminders}
                    checked={!!profile.email_subscription_reminders}
                    onChange={() => handleNotificationChange('email_subscription_reminders')}
                    disabled={!isEditing}
                  />
                </div>

                <div className="profile-col profile-col-right">
                  <div className="notifications-title">{t.whatNoti}</div>

                  <NotificationRow
                    label={t.exeReminders}
                    checked={!!profile.whatsapp_exercise_reminders}
                    onChange={() => handleNotificationChange('whatsapp_exercise_reminders')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.sessionReminders}
                    checked={!!profile.whatsapp_session_reminders}
                    onChange={() => handleNotificationChange('whatsapp_session_reminders')}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </GlassCard>

            <GlassCard style={{ padding: '10px', maxWidth: '775px', marginLeft: '50px' }}>
              <div className="profile-section notifications-section">
                <div className="profile-col profile-col-left">
                  <div className="notifications-title">{t.webNoti}</div>

                  <NotificationRow
                    label={t.exeReminders}
                    checked={!!profile.website_exercise_reminders}
                    onChange={() => handleNotificationChange('website_exercise_reminders')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.sessionReminders}
                    checked={!!profile.website_session_reminders}
                    onChange={() => handleNotificationChange('website_session_reminders')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.productUpdates}
                    checked={!!profile.website_product_updates}
                    onChange={() => handleNotificationChange('website_product_updates')}
                    disabled={!isEditing}
                  />

                  <NotificationRow
                    label={t.subReminders}
                    checked={!!profile.website_subscription_reminders}
                    onChange={() => handleNotificationChange('website_subscription_reminders')}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </GlassCard>

            {isEditing && (
              <div className="profile-save-wrapper">
                <button
                  type="submit"
                  className="profile-btn profile-save-btn"
                  disabled={saving}
                >
                  {saving ? t.save2 : t.save}
                </button>
              </div>
            )}
          </form>
        </div>
      </main>
      <footer>
        <FooterPlatform />
      </footer>
    </div>
  );
}

function EditableRow({ label, value, editable, isEditing, onChange, options = [] }) {
  const showInput = editable && isEditing;
  const isDropdown = options.length > 0;

  return (
    <div className="profile-field-row">
      <div className="profile-field-label">{label}</div>

      <div className="profile-field-value-wrapper">
        {showInput ? (
          isDropdown ? (
            <select
              className="profile-input"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">Select {label}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="profile-input"
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            />
          )
        ) : (
          <div className="profile-field-value">{value}</div>
        )}

        <div className="profile-field-underline" />
      </div>
    </div>
  );
}

function NotificationRow({ label, checked, onChange, disabled = false }) {
  return (
    <label className="notification-row">
      <span className="notification-label">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="notification-checkbox"
      />
    </label>
  );
}